import { Link } from 'react-router-dom'
import { BarChart3, LayoutGrid, Mic, Users, CreditCard, Bot, Settings, FolderKanban, Bell } from 'lucide-react'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from './ui/sidebar'
import { SyncSimpleMark } from './brand/SyncSimpleMark'
import { useTeamAccess } from '../contexts/AuthContext'

// 2026-10-08: Billing temporarily hidden from the sidebar on explicit
// request. Single flag so it's a one-line revert rather than re-deriving
// the isAdmin-gated block below from scratch later.
const SHOW_BILLING_NAV = false

export type DashboardSection =
  | 'dashboard'
  | 'standups'
  | 'standups-checkins'
  | 'standups-summary'
  | 'agents'
  | 'onboarding'
  | 'team-management'
  | 'projects'
  | 'config'
  | 'alerts'
  | 'billing'

interface AppSidebarProps {
  activeSection: DashboardSection
  onNavigate: (section: DashboardSection) => void
  /**
   * @deprecated Team Management and Billing are now their own routed pages
   * (see teamManagementHref/billingHref below) — these callbacks are no
   * longer invoked. Kept optional, not removed, so pages that still pass
   * them (e.g. TeamDashboard.tsx's modal-based flow) don't need to change
   * in this same pass.
   */
  onOpenTeamManagement?: () => void
  /** @deprecated see onOpenTeamManagement */
  onOpenBilling?: () => void
  /** Used to build the link to the onboarding page (/install/claude-code?teamId=...). */
  teamId?: string
}

/**
 * Dark sidebar shell matching syncsimple-v2's AppSidebar/design system.
 * Nav items act on the single-page dashboard (scroll/section state) rather
 * than routing, since this app doesn't have separate routes per section.
 */
export function AppSidebar({
  activeSection,
  onNavigate,
  teamId,
}: AppSidebarProps) {
  const checkinsHref = teamId ? `/dashboard/team/${teamId}/standups/check-ins` : '/dashboard'
  const summaryHref = teamId ? `/dashboard/team/${teamId}/standups/summary` : '/dashboard'
  const agentsHref = teamId ? `/dashboard/team/${teamId}/connectors` : '/dashboard'
  const teamManagementHref = teamId ? `/dashboard/team/${teamId}/team-management` : '/dashboard'
  const projectsHref = teamId ? `/dashboard/team/${teamId}/projects` : '/dashboard'
  const configHref = teamId ? `/dashboard/team/${teamId}/config` : '/dashboard'
  const alertsHref = teamId ? `/dashboard/team/${teamId}/alerts` : '/dashboard'
  const billingHref = teamId ? `/dashboard/team/${teamId}/billing` : '/dashboard'

  // Role gate for the "Admin" group below. Previously this group rendered
  // unconditionally for every team member -- each destination page enforced
  // admin-only at the content level (its own `isAdmin` state), not here, so
  // a plain member could navigate straight to /team-management or /billing
  // by URL and see the nav entry besides. See the ROLE AUDIT comment in
  // Projects.tsx and the ALERTING_PERMISSIONS_PLAN.md note that flagged this.
  //
  // Per-item split (matches what each destination page already enforces,
  // not a new policy):
  //   - Team Management, Billing: admin-only. Every write on these pages
  //     goes through an admin-gated edge function (manage-pods,
  //     invite-team-members, ...) or is explicitly admin-only by product
  //     decision (BillingManagement.tsx) -- a manager has no real action here.
  //   - Projects, Config: admin AND manager. Projects.tsx already tracks
  //     `isManager` (Attribution rules: admin edits, manager views -- see its
  //     ROLE AUDIT comment); Config hosts StandupConfiguration/
  //     PodStandupOverrides, which a pod's manager can already edit for their
  //     own pod(s). Hiding these from managers would nav-lock a page they
  //     can legitimately use.
  // `loading` is treated as "not yet admin/manager" -- the group simply
  // appears once role resolves, rather than flashing admin content first.
  const { role, loading: accessLoading } = useTeamAccess(teamId)
  const isAdmin = role === 'admin' || role === 'owner'
  const isManager = role === 'manager'
  const showAdminGroup = !accessLoading && (isAdmin || isManager)
  const showTeamSummary = !accessLoading && (isAdmin || isManager)

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <div className="flex items-center gap-2 px-2 py-1">
          <div className="flex h-7 w-7 shrink-0 items-center justify-center">
            <SyncSimpleMark className="h-6 w-6" />
          </div>
          <span className="text-[15px] font-semibold text-sidebar-foreground group-data-[collapsible=icon]:hidden">
            SyncSimple
          </span>
        </div>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>General</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  isActive={activeSection === 'dashboard'}
                  tooltip="Dashboard"
                  onClick={() => onNavigate('dashboard')}
                >
                  <LayoutGrid />
                  <span>Dashboard</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={activeSection === 'standups-checkins'} tooltip="Standups">
                  <Link to={checkinsHref}>
                    <Mic />
                    <span>Standups</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              {showTeamSummary && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild isActive={activeSection === 'standups-summary'} tooltip="Team Summary">
                    <Link to={summaryHref}>
                      <BarChart3 />
                      <span>Team Summary</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={activeSection === 'agents' || activeSection === 'onboarding'}
                  tooltip="Connectors"
                >
                  <Link to={agentsHref}>
                    <Bot />
                    <span>Connectors</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {showAdminGroup && (
          <SidebarGroup>
            <SidebarGroupLabel>Admin</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {isAdmin && (
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      asChild
                      isActive={activeSection === 'team-management'}
                      tooltip="Team Management"
                    >
                      <Link to={teamManagementHref}>
                        <Users />
                        <span>Team Management</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )}
                <SidebarMenuItem>
                  <SidebarMenuButton asChild isActive={activeSection === 'projects'} tooltip="Projects">
                    <Link to={projectsHref}>
                      <FolderKanban />
                      <span>Projects</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                {/* Billing nav item temporarily hidden 2026-10-08 (explicit
                    request) -- flip SHOW_BILLING_NAV back to true to restore
                    it. The /billing route itself (BillingManagement.tsx) is
                    untouched and still reachable by direct URL; this only
                    removes the sidebar entry. */}
                {SHOW_BILLING_NAV && isAdmin && (
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild isActive={activeSection === 'billing'} tooltip="Billing">
                      <Link to={billingHref}>
                        <CreditCard />
                        <span>Billing</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )}
                <SidebarMenuItem>
                  <SidebarMenuButton asChild isActive={activeSection === 'config'} tooltip="Config">
                    <Link to={configHref}>
                      <Settings />
                      <span>Config</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild isActive={activeSection === 'alerts'} tooltip="Alerts">
                    <Link to={alertsHref}>
                      <Bell />
                      <span>Alerts</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      {/* Support-triage aid: __APP_VERSION__ is a "v<commit count>" string
          injected at build time (see vite.config.ts) — auto-increments on
          every deploy with nothing to remember to bump, so when a user
          reports an issue you can ask "what version does the sidebar say"
          and know exactly which build they're on. */}
      <SidebarFooter>
        <div className="px-2 py-1 text-xs text-sidebar-foreground/40 group-data-[collapsible=icon]:hidden">
          {__APP_VERSION__}
        </div>
      </SidebarFooter>
    </Sidebar>
  )
}
